import React from 'react';
import { useSelector } from 'react-redux';
import styled, { useTheme } from 'styled-components';

import { Block, Icon, Level, Measurement } from '@deckee/deck-hand';

import { telemetry } from '../../../services/backend';
import { getDisclosureState } from '../../../services/log-trips';
import {
  ROUTE_LOCATION_DISCLOSURE,
  ROUTE_TRIP_ACTIVE,
} from '../../../services/route/route-names';
import { transitionToNextRoute } from '../../../services/route/route-navigation';
import { selectUnitsConfig } from '../../../services/store/modules/app-controls';
import {
  formatDistanceDisplay,
  formatSpeedDisplay,
} from '../../helpers/data-helpers';

const TripControls = styled.div`
  display: flex;
  justify-content: space-evenly;
  pointer-events: all;
  cursor: pointer;
`;

const TripInfoBlock = styled.div`
  background: ${(props) => props.theme.colors.white};
  border-radius: 72px;
  position: relative;
  min-width: 250px;
`;

const IconBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.white};
  width: 46px;
  height: 46px;
  box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.15);
  border-radius: 70px;

  transform: rotate(${(props) => props.rotation || 0}deg);
`;

const ConnectedActiveTripButton = ({ className }) => {
  const unitsConfig = useSelector(selectUnitsConfig);
  const showingActiveTripButton = useSelector(
    (state) => state.appControls.showingActiveTripButton,
  );
  const logTrips = useSelector((state) => state.logTrips);
  const geoService = useSelector((state) => state.geoService);

  const theme = useTheme();

  const { activeTrip, currentSpeed, currentDistance } = logTrips;
  const { currentHeading } = geoService;

  const speedToDisplay = formatSpeedDisplay(currentSpeed, unitsConfig);
  const distanceToDisplay = formatDistanceDisplay(currentDistance, unitsConfig);

  const onButtonClick = async () => {
    try {
      const disclosureState = await getDisclosureState();

      if (!disclosureState) {
        transitionToNextRoute(ROUTE_LOCATION_DISCLOSURE);
      } else {
        transitionToNextRoute(ROUTE_TRIP_ACTIVE);
      }
    } catch (error) {
      telemetry(
        telemetry.DEBUG,
        `[showingActiveTripButton.getDisclosureState] Error: ${error}`,
      );
      // TODO: Show some error message that trip could not be started
    }
  };

  return (
    showingActiveTripButton &&
    activeTrip && (
      <TripControls onClick={onButtonClick} className={className}>
        <TripInfoBlock>
          <Level>
            <Level.Item>
              <IconBlock rotation={currentHeading}>
                <Icon name="direction-arrow-up" fill={theme.colors.info} />
              </IconBlock>
            </Level.Item>

            <Level.Item>
              <Measurement>
                <Block>
                  <Measurement.Value level={6}>
                    {speedToDisplay.value}
                  </Measurement.Value>
                  <Measurement.Unit level={6}>
                    {speedToDisplay.unit}
                  </Measurement.Unit>
                </Block>
                <Measurement.Description>Speed</Measurement.Description>
              </Measurement>
            </Level.Item>

            <Level.Item>
              <Measurement>
                <Block>
                  <Measurement.Value level={6}>
                    {distanceToDisplay.value}
                  </Measurement.Value>
                  <Measurement.Unit level={6}>
                    {distanceToDisplay.unit}
                  </Measurement.Unit>
                </Block>
                <Measurement.Description>Distance</Measurement.Description>
              </Measurement>
            </Level.Item>

            <Level.Item />
          </Level>
        </TripInfoBlock>
      </TripControls>
    )
  );
};

ConnectedActiveTripButton.defaultProps = {
  className: undefined,
};
export default ConnectedActiveTripButton;
